import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Shield, Eye, EyeOff, Check, X, Loader2, Lock } from "lucide-react";

const PASSWORD_RULES = [
    { test: (p) => p.length >= 8, label: "Au moins 8 caracteres" },
    { test: (p) => /[A-Z]/.test(p), label: "Au moins une lettre majuscule" },
    { test: (p) => /[a-z]/.test(p), label: "Au moins une lettre minuscule" },
    { test: (p) => /[0-9]/.test(p), label: "Au moins un chiffre" },
];

export const ForceChangePasswordPage = () => {
    const { token, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const allRulesPassed = PASSWORD_RULES.every((r) => r.test(newPassword));
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const canSubmit = allRulesPassed && passwordsMatch && !loading;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/auth/change-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ new_password: newPassword }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Erreur lors du changement");
            }

            toast.success("Mot de passe modifie avec succes !");
            // Re-fetch user data to clear must_change_password flag
            await checkAuth();
            // Navigate to dashboard after successful password change
            navigate("/dashboard", { replace: true });
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="force-change-password-page">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center space-y-3">
                    <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Shield className="w-7 h-7 text-amber-600" />
                    </div>
                    <CardTitle className="text-xl">Changement de mot de passe requis</CardTitle>
                    <CardDescription>
                        Pour des raisons de securite, veuillez changer votre mot de passe avant de continuer.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="new-password">Nouveau mot de passe</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="new-password"
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="pl-9 pr-10"
                                    placeholder="Votre nouveau mot de passe"
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Password strength rules */}
                        <div className="space-y-1.5 p-3 rounded-lg bg-muted/50">
                            {PASSWORD_RULES.map((rule, i) => {
                                const passed = rule.test(newPassword);
                                return (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        {newPassword ? (
                                            passed ? (
                                                <Check className="w-3.5 h-3.5 text-green-500" />
                                            ) : (
                                                <X className="w-3.5 h-3.5 text-red-500" />
                                            )
                                        ) : (
                                            <div className="w-3.5 h-3.5 rounded-full border border-muted-foreground/30" />
                                        )}
                                        <span className={newPassword ? (passed ? "text-green-600" : "text-red-600") : "text-muted-foreground"}>
                                            {rule.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div>
                            <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="confirm-password"
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-9"
                                    placeholder="Confirmez le mot de passe"
                                    data-testid="confirm-password-input"
                                />
                            </div>
                            {confirmPassword && !passwordsMatch && (
                                <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={!canSubmit}
                            data-testid="change-password-submit"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Shield className="w-4 h-4 mr-2" />
                            )}
                            Changer le mot de passe
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
